import { useEffect, useRef, useState } from 'react';
import { get, post, newFormKey, ApiError, type Booking, type FollowUp, type Lookups } from '../lib/api.ts';
import { Alert, Modal, Spinner } from '../components/ui.tsx';
import { AttachmentsReminder } from '../booking/attachments/AttachmentsReminder.tsx';
import { displayPhone } from '../lib/format.ts';
import { BookingForm } from '../booking/BookingForm.tsx';
import { AttachmentPicker } from '../booking/attachments/AttachmentPicker.tsx';
import { CreatedWithUploads } from '../booking/attachments/CreatedWithUploads.tsx';
import { missingDescription, type StagedFile } from '../booking/attachments/uploadQueue.ts';
import {
  InitialPaymentSection, emptyInitialPayment, initialPaymentsBody, uploadPartsEvidence, validateInitialPayment,
  type InitialPaymentDraft,
} from '../booking/InitialPaymentSection.tsx';
import { PAYMENT_NOTE, PAYMENT_REQUIRED_OPS, attachmentHint, missingAttachment, ATTACHMENT_REQUIRED_MSG } from '../booking/paymentRules.ts';

interface Prefill { followUpId: string; customerName: string; phone: string; altPhone?: string | null; alreadyConverted: boolean }

/**
 * «إنشاء حجز» من متابعة: نفس نموذج الحجز المعتمد وبنفس ترتيب الحقول وبنفس قواعد الخادم.
 * الاسم والهاتف يأتيان من المتابعة، والهاتف يُثبّته الخادم (لا يُرسل من الواجهة).
 *
 * **ومعه إثبات الدفع كما في «حجز جديد»** (قرار المالك: «ليه لما بعمل حجز من المتابعة
 * مفيش الدفع وإنه يرفع إثبات الدفع من الأول من هنا؟»). والحجز من متابعة بيعٌ كغيره:
 * لو بقي بابًا بلا دفعة لصار هو الطريق الذي يُفلت منه كلُّ من لا يريد تسجيل ما حصّله.
 * والخادم يردّ الاثنين في معاملة واحدة — إمّا حجزٌ ودفعته أو لا شيء.
 */
export function BookingFromFollowUp({ followUp, onClose, onCreated, isAdminUser = false }:
  { followUp: FollowUp; onClose: () => void; onCreated: (f: FollowUp, b: Booking) => void; isAdminUser?: boolean }) {
  const [lookups, setLookups] = useState<Lookups | null>(null);
  const [prefill, setPrefill] = useState<Prefill | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const formKey = useRef(newFormKey());     // كود النموذج (0179): النت قطع بعد الحفظ ⇒ الضغطة الثانية تأخذ نفس الحجز
  const [staged, setStaged] = useState<StagedFile[]>([]);
  /** تذكير المرفقات (قرار المالك): نافذةٌ حين يُضغط الحفظ بلا ملفّ واحد */
  const [remind, setRemind] = useState(false);
  const [created, setCreated] = useState<{ followUp: FollowUp; booking: Booking } | null>(null);
  const [payment, setPayment] = useState<InitialPaymentDraft>(emptyInitialPayment);
  const [payErrors, setPayErrors] = useState<Record<string, string>>({});
  const [evidenceNote, setEvidenceNote] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      get<Lookups>('/lookups'),
      get<Prefill>(`/follow-ups/${followUp.id}/booking-prefill`),
    ]).then(([l, p]) => { setLookups(l); setPrefill(p); })
      .catch(err => setError(err instanceof ApiError ? err.message : 'تعذّر تحميل بيانات النموذج'));
  }, [followUp.id]);

  async function create(body: Record<string, unknown>) {
    if (staged.some(missingDescription)) { setError('أكمل وصف المرفقات من نوع «مستند آخر متعلق بالحجز» قبل الحفظ'); return; }
    // ولا حجز بلا ورق — ومع «تعديل» و«الغاء» مستندُهما بعينه (نفس قاعدة «حجز جديد»)
    const lack = missingAttachment(String(body.operationType ?? ''), staged);
    if (lack) { setError(lack); if (lack === ATTACHMENT_REQUIRED_MSG) setRemind(true); return; }

    // لا حجز جديدٍ أو مكرر بلا دفعة — والخادم يردّها أيضًا (PAYMENT_REQUIRED)
    const mustPay = PAYMENT_REQUIRED_OPS.has(String(body.operationType ?? ''));
    if (mustPay && !payment.enabled) {
      setError('الحجز الجديد والمكرر لازم يتسجّل معاه المحصَّل — املأ «إثبات الدفع».');
      return;
    }
    const pe = validateInitialPayment(mustPay ? { ...payment, enabled: true } : payment);
    setPayErrors(pe);
    if (Object.keys(pe).length) { setError('أكمل بيانات الدفعة الأولى أو ألغِ اختيار «يوجد دفع الآن»'); return; }

    setError(null); setEvidenceNote(null); setBusy(true);
    try {
      const r = await post<{ followUp: FollowUp; booking: Booking; payments: { id: string }[] }>(
        `/follow-ups/${followUp.id}/booking`, { ...body, payments: initialPaymentsBody(payment) },
        { 'idempotency-key': formKey.current });

      // الإثبات بعد وجود الدفعات، كلٌّ إلى دفعته — وفشله لا يُبطل ما حُفظ
      if ((r.payments ?? []).length && !(await uploadPartsEvidence(payment, r.payments.map(x => x.id)))) {
        setEvidenceNote('حُفظ الحجز والدفعة، لكن تعذّر رفع الإثبات — ارفعه من تفاصيل الحجز.');
      }

      if (staged.length || evidenceNote) setCreated(r); else onCreated(r.followUp, r.booking);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'تعذّر إنشاء الحجز');
    } finally { setBusy(false); }
  }

  if (created) {
    return (
      <Modal title={`الحجز ${created.booking.bookingNo}`} onClose={() => onCreated(created.followUp, created.booking)}>
        {evidenceNote && <div className="mb-3"><Alert kind="info">{evidenceNote}</Alert></div>}
        <CreatedWithUploads bookingId={created.booking.id} bookingNo={created.booking.bookingNo} files={staged}
          onFinish={() => onCreated(created.followUp, created.booking)} />
      </Modal>
    );
  }

  return (
    <Modal size="full" title={`إنشاء حجز من المتابعة ${followUp.followUpNo}`} onClose={onClose}>
      {remind && <AttachmentsReminder onClose={() => { setRemind(false); document.querySelector<HTMLElement>('[data-attachments]')?.scrollIntoView?.({ behavior: 'smooth', block: 'center' }); }} />}
      {!lookups || !prefill ? <Spinner /> : (
        <BookingForm
          formLabel="إنشاء حجز"
          // حيث الدفعة إجبارية هي جزءٌ من الحجز لا فعلٌ زائد عليه
          submitLabel={draft => (payment.enabled && !PAYMENT_REQUIRED_OPS.has(draft.operationType)
            ? 'حفظ الحجز وتسجيل الدفع' : 'إنشاء الحجز')}
          lookups={lookups}
          salesName={followUp.salesName ?? ''}
          isAdminUser={isAdminUser}
          lockedPhone={prefill.phone}
          initial={{ customerName: prefill.customerName, altPhone: prefill.altPhone ? displayPhone(prefill.altPhone) : '' }}
          busy={busy}
          error={error}
          banner={
            <>
              <Alert kind="info">رقم الهاتف من المتابعة: <span dir="ltr" className="tabular-nums">{displayPhone(prefill.phone)}</span></Alert>
            </>
          }
          footer={
            draft => (
              <div className="grid gap-3 xl:grid-cols-2">
                <InitialPaymentSection value={payment} onChange={setPayment} errors={payErrors} busy={busy} destinations={lookups.paymentDestinations}
                  salePrice={draft.salePrice}
                  alwaysOn={PAYMENT_REQUIRED_OPS.has(draft.operationType)}
                  note={PAYMENT_NOTE[draft.operationType]} />
                <AttachmentPicker files={staged} onChange={setStaged} busy={busy}
                  required note={attachmentHint(draft.operationType)} />
              </div>
            )
          }
          onSubmit={body => create(body)}
        />
      )}
    </Modal>
  );
}
